import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, LockKeyhole, Pencil, Play, Square } from 'lucide-react';
import MarkdownText from './MarkdownText';
import ReadingSpeechBubble, { ReadingSpeechAvatar } from './ReadingSpeechBubble';
import ReadingContentItem, { splitReadingSpeech as splitSpeechQA } from './ReadingContentItem';
import { playPracticeSound } from '../services/soundEffects';
import useCardFlipAudio from '../hooks/useCardFlipAudio';
import { isTtsAutoplayEnabled } from '../services/TtsPreferenceService';
import useReadingAudioFollow from '../hooks/useReadingAudioFollow';
import { readingAudioText } from './ReadingAudioControls';
import { t } from '../i18n';
import { splitReadingStoryLines } from '../utils/readingStory';
import './ReadingArticlePractice.css';

const REPEAT_INPUT_LOCK_MS = 560;
const REPEAT_FEEDBACK_DURATION_MS = 720;

function normalizeNewlines(text) {
  return String(text || '').replace(/\r\n/g, '\n');
}

function getLevelUpContextText(item) {
  return item?.type === 'speech'
    ? splitSpeechQA(item.text).answer
    : String(item?.text || '');
}

function isSpeechPracticeTarget(text) {
  return normalizeNewlines(text)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .length >= 2;
}

export function getReadingArticleParts(card) {
  const title = card?.readingTitle || card?.front || '';
  const childById = new Map((card?.readingChildren || []).map((child) => [
    String(child?.id || child?._id || ''),
    child,
  ]));

  const legacyDialogSegments = Array.isArray(card?.dialogTurns)
    ? card.dialogTurns.map((turn) => {
      if (turn?.type === 'situation') {
        return { type: 'scene', text: turn.text || turn.nativeText || '', practice: false };
      }
      if (turn?.type === 'story') {
        return { type: 'story', text: turn.text || turn.nativeText || '', practice: true };
      }
      return {
        type: 'speech',
        speaker: turn?.speaker === 'B' ? 'B' : 'A',
        text: turn?.answerText || turn?.nativeText || turn?.text || '',
        practice: turn?.practice !== false,
      };
    }).filter((segment) => segment.text)
    : null;
  const sourceSegments = Array.isArray(card?.readingSegments) && card.readingSegments.length > 0
    ? card.readingSegments
    : legacyDialogSegments?.length ? legacyDialogSegments
      // Preserve stored legacy units; do not merge/rewrite learned children.
      : (Array.isArray(card?.readingSentences) && card.readingSentences.length ? card.readingSentences : [card?.readingContent || card?.back || ''])
        .map(text => ({ type: 'story', text }));

  const displayItems = sourceSegments.flatMap((segment, segmentIndex) => {
    const type = segment.type === 'scene' || segment.type === 'situation'
      ? 'scene'
      : segment.type === 'speech' || segment.speaker
        ? 'speech'
        : 'story';
    const text = String(segment.text || segment.nativeText || '').trim();
    if (!text) return [];
    if (type === 'story') {
      const splitSentences = splitReadingStoryLines(text);
      return splitSentences.map((sentence, sentenceIndex) => {
        const childCardId = splitSentences.length === 1 ? (segment.childCardId || null) : null;
        const child = childCardId ? childById.get(String(childCardId)) : null;
        return {
          type: 'story',
          segmentIndex, sentenceIndex,
          text: sentence,
          practice: child?.practiceAvailable !== false,
          childCardId,
        };
      });
    }
    const isPracticeSpeech = type === 'speech' && isSpeechPracticeTarget(text);
    const child = segment.childCardId ? childById.get(String(segment.childCardId)) : null;
    return [{
      type,
      segmentIndex, sentenceIndex: 0,
      speaker: segment.speaker === 'B' ? 'B' : 'A',
      text,
      practice: isPracticeSpeech && child?.practiceAvailable !== false,
      passThrough: type === 'speech' && !isPracticeSpeech,
      childCardId: segment.childCardId || null,
      ...(segment.practiceMode === 'once' ? { practiceMode: 'once' } : {}),
    }];
  });
  const practiceItems = displayItems.filter((item) => item.practice);
  const sentences = practiceItems.map((item) => item.text);

  return {
    title,
    sentences,
    sourceText: displayItems.map((item) => item.text).join('\n\n'),
    displayItems,
    practiceItems,
    characterNames: card?.readingCharacterNames || card?.dialogCharacterNames || { A: 'A', B: 'B' },
  };
}

function getPracticeIndexForDisplayIndex(displayItems = [], displayIndex = 0) {
  return displayItems
    .slice(0, Math.max(0, displayIndex))
    .filter((item) => item.practice)
    .length;
}

function canDisplayItemBeCurrent(item) {
  return Boolean(item?.practice) || Boolean(item?.passThrough);
}

function getNextCurrentDisplayIndex(displayItems = [], startIndex = 0) {
  if (!Array.isArray(displayItems) || displayItems.length === 0) return 0;
  const safeStart = Math.max(0, startIndex);
  if (safeStart >= displayItems.length) return displayItems.length;
  const nextIndex = displayItems.findIndex((item, index) => index >= safeStart && canDisplayItemBeCurrent(item));
  return nextIndex >= 0 ? nextIndex : displayItems.length;
}

function ReadingArticlePractice({
  card,
  resetKey = 0,
  targetRepeats = 10,
  onSentenceRepeat,
  onSentenceAutoRepeat,
  onCardComplete,
  onProgressChange,
  onSwipeLeft,
  onSwipeRight,
  readOnly = false,
  keyboardShortcutsEnabled = false,
  autoRepeatAvailable = true,
  autoRepeatHoldMs = 1000,
  getSentenceScorePreview,
  getTargetRepeatsForChild,
  canUseTts = true,
  onMembershipRequired,
  readingAudio,
  onActiveSentenceChange,
}) {
  const {
    readingTitle,
    front,
    dialogTurns,
    readingSegments,
    readingCharacterNames,
    dialogCharacterNames,
    readingContent,
    back,
    readingSentences,
  } = card || {};
  const { title, sentences, displayItems, characterNames } = useMemo(
    () => getReadingArticleParts({
      readingTitle,
      front,
      dialogTurns,
      readingSegments,
      readingCharacterNames,
      dialogCharacterNames,
      readingContent,
      back,
      readingSentences,
      readingChildren: card?.readingChildren,
    }),
    [
      readingTitle,
      front,
      dialogTurns,
      readingSegments,
      readingCharacterNames,
      dialogCharacterNames,
      readingContent,
      back,
      readingSentences,
      card?.readingChildren,
    ]
  );
  const cardIdentity = String(card?.id || card?._id || '');
  const readingContentSignature = useMemo(() => JSON.stringify({
    title,
    sentences,
    displayItems,
    characterNames,
  }), [title, sentences, displayItems, characterNames]);
  const [sentenceIndex, setSentenceIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(() => getNextCurrentDisplayIndex(displayItems, 0));
  const [repeatProgress, setRepeatProgress] = useState({ key: '', count: 0 });
  const [speechSide, setSpeechSide] = useState('question');
  const playCardFlip = useCardFlipAudio();
  const [repeatEffect, setRepeatEffect] = useState(null);
  const [isSentenceAdvancePending, setIsSentenceAdvancePending] = useState(false);
  const [isAutoRepeatCharging, setIsAutoRepeatCharging] = useState(false);
  const [isAutoRepeatBursting, setIsAutoRepeatBursting] = useState(false);
  const isBrowserSpeaking = readingAudio?.state?.status === 'playing';
  const audioFollow = useReadingAudioFollow(readingAudio?.state);
  const currentSectionRef = useRef(null);
  const drawingCanvasRef = useRef(null);
  const drawingStateRef = useRef({ isDrawing: false, lastX: 0, lastY: 0 });
  const speechAnswerViewedRef = useRef(false);
  const sentenceAdvanceTimerRef = useRef(null);
  const sentenceAdvancePendingRef = useRef(false);
  const autoRepeatTimerRef = useRef(null);
  const autoRepeatPointerStartRef = useRef(null);
  const suppressNextStatusClickRef = useRef(false);
  const repeatInputLockTimerRef = useRef(null);
  const repeatInputLockedRef = useRef(false);
  const hasPositionedCurrentItemRef = useRef(false);
  const repeatPlanRef = useRef(null);
  const reviewedOnceRef = useRef(new Set());
  const displayItemsRef = useRef(displayItems);
  displayItemsRef.current = displayItems;
  const stopBrowserSpeech = useCallback(() => {
    readingAudio?.engine?.stop();
  }, [readingAudio?.engine]);

  useEffect(() => {
    hasPositionedCurrentItemRef.current = false;
    setSentenceIndex(0);
    reviewedOnceRef.current.clear();
    setDisplayIndex(getNextCurrentDisplayIndex(displayItemsRef.current, 0));
    setRepeatProgress({ key: '', count: 0 });
    setSpeechSide('question');
    speechAnswerViewedRef.current = false;
    setRepeatEffect(null);
    setIsSentenceAdvancePending(false);
    sentenceAdvancePendingRef.current = false;
    if (sentenceAdvanceTimerRef.current) {
      clearTimeout(sentenceAdvanceTimerRef.current);
      sentenceAdvanceTimerRef.current = null;
    }
    setIsAutoRepeatCharging(false);
    setIsAutoRepeatBursting(false);
    repeatInputLockedRef.current = false;
    if (repeatInputLockTimerRef.current) {
      clearTimeout(repeatInputLockTimerRef.current);
      repeatInputLockTimerRef.current = null;
    }

    if (typeof document !== 'undefined') {
      const scrollingElement = document.scrollingElement || document.documentElement;
      if (scrollingElement) scrollingElement.scrollTop = 0;
    }
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      } catch {
        window.scrollTo(0, 0);
      }
    }
  }, [cardIdentity, readingContentSignature, resetKey]);

  useEffect(() => () => {
    stopBrowserSpeech();
    if (autoRepeatTimerRef.current) {
      clearTimeout(autoRepeatTimerRef.current);
      autoRepeatTimerRef.current = null;
    }
    if (repeatInputLockTimerRef.current) {
      clearTimeout(repeatInputLockTimerRef.current);
      repeatInputLockTimerRef.current = null;
    }
    if (sentenceAdvanceTimerRef.current) {
      clearTimeout(sentenceAdvanceTimerRef.current);
      sentenceAdvanceTimerRef.current = null;
    }
    sentenceAdvancePendingRef.current = false;
  }, [stopBrowserSpeech]);

  useEffect(() => {
    if (!hasPositionedCurrentItemRef.current) {
      hasPositionedCurrentItemRef.current = true;
      return;
    }
    if (currentSectionRef.current?.scrollIntoView) {
      currentSectionRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [sentenceIndex, displayIndex]);

  const totalSentences = Math.max(1, sentences.length);
  const activeSentenceIndex = Array.isArray(displayItems) && displayItems.length > 0
    ? getPracticeIndexForDisplayIndex(displayItems, displayIndex)
    : sentenceIndex;
  const currentItem = displayItems[displayIndex] || null;
  const currentText = currentItem?.text || '';
  const isCurrentPracticeTarget = currentItem?.practice === true;
  const isCurrentSpeech = currentItem?.type === 'speech';
  const isCurrentOnce = isCurrentSpeech && currentItem?.practiceMode === 'once';
  const currentSpeaker = currentItem?.speaker === 'B' ? 'B' : 'A';
  const currentSpeechParts = isCurrentSpeech ? splitSpeechQA(currentText) : null;
  const canFlipCurrentSpeech = isCurrentSpeech && isSpeechPracticeTarget(currentText);
  const currentDisplayText = isCurrentSpeech
    ? currentSpeechParts?.[speechSide] || currentText
    : currentText;
  const hasDisplayItems = Array.isArray(displayItems) && displayItems.length > 0;
  const fallbackTargetRepeats = Math.max(1, Number(targetRepeats) || 1);
  // Grade updates must not change an in-progress visit's counter or article progress totals.
  if (!repeatPlanRef.current ||
    repeatPlanRef.current.cardIdentity !== cardIdentity ||
    repeatPlanRef.current.contentSignature !== readingContentSignature ||
    repeatPlanRef.current.resetKey !== resetKey) {
    repeatPlanRef.current = {
      cardIdentity,
      contentSignature: readingContentSignature,
      resetKey,
      pageTargetRepeats: fallbackTargetRepeats,
      version: (repeatPlanRef.current?.version || 0) + 1,
      targets: hasDisplayItems ? displayItems.map((item) => {
        if (item.practiceMode === 'once') return 1;
        const child = item.childCardId && card?.readingChildren?.find((candidate) => (
          String(candidate.id || candidate._id) === String(item.childCardId)
        ));
        return Math.max(1, Number(child && getTargetRepeatsForChild?.(child)) || fallbackTargetRepeats);
      }) : Array(totalSentences).fill(fallbackTargetRepeats),
    };
  }
  const currentRepeatKey = `${repeatPlanRef.current.version}:${hasDisplayItems ? displayIndex : sentenceIndex}`;
  const sentenceRepeatCount = repeatProgress.key === currentRepeatKey ? repeatProgress.count : 0;
  const currentTargetRepeats = repeatPlanRef.current.targets[hasDisplayItems ? displayIndex : sentenceIndex]
    || fallbackTargetRepeats;
  const practiceTargets = hasDisplayItems
    ? repeatPlanRef.current.targets.filter((_, index) => displayItems[index].practice !== false)
    : repeatPlanRef.current.targets;
  const totalTargetRepeats = practiceTargets.reduce((sum, target) => sum + target, 0);
  const completedTargetRepeats = practiceTargets
    .slice(0, activeSentenceIndex)
    .reduce((sum, target) => sum + target, 0);
  const completedSentenceRepeats = completedTargetRepeats +
    (isCurrentPracticeTarget ? Math.min(sentenceRepeatCount, currentTargetRepeats) : 0);
  const progressPercent = totalTargetRepeats > 0
    ? Math.min(100, (completedSentenceRepeats / totalTargetRepeats) * 100)
    : 0;

  useEffect(() => {
    onProgressChange?.({
      completedRepeats: completedSentenceRepeats,
      totalRepeats: totalTargetRepeats,
      percent: progressPercent,
    });
  }, [completedSentenceRepeats, onProgressChange, progressPercent, totalTargetRepeats]);

  const currentChildId = currentItem?.childCardId || null;
  const suggestedContextPre = isCurrentSpeech ? getLevelUpContextText(displayItems[displayIndex - 1]) : '';
  const suggestedContextAfter = isCurrentSpeech ? getLevelUpContextText(displayItems[displayIndex + 1]) : '';

  useEffect(() => {
    const speech = isCurrentSpeech ? splitSpeechQA(currentText) : null;
    onActiveSentenceChange?.({
      cardId: cardIdentity,
      resetKey,
      childCardId: !readOnly && isCurrentPracticeTarget ? currentChildId : null,
      isAdvancing: isSentenceAdvancePending,
      readingTitle: title,
      suggestedQuestion: speech ? speech.question : '',
      suggestedAnswer: speech ? speech.answer : currentText,
      suggestedContextPre,
      suggestedContextAfter,
    });
  }, [cardIdentity, resetKey, currentChildId, currentText, isCurrentSpeech, isCurrentPracticeTarget,
    isSentenceAdvancePending, onActiveSentenceChange, readOnly, suggestedContextPre, suggestedContextAfter, title]);

  useEffect(() => {
    stopBrowserSpeech();
  }, [card?.id, card?._id, displayIndex, resetKey, sentenceIndex, stopBrowserSpeech]);

  useEffect(() => {
    setSpeechSide('question');
    speechAnswerViewedRef.current = false;
  }, [sentenceIndex, displayIndex, currentText]);

  const moveToDisplayIndex = (nextDisplayIndex) => {
    if (!Array.isArray(displayItems) || displayItems.length === 0) return false;
    const resolvedDisplayIndex = getNextCurrentDisplayIndex(displayItems, nextDisplayIndex);
    if (resolvedDisplayIndex < 0 || resolvedDisplayIndex >= displayItems.length) return false;

    setDisplayIndex(resolvedDisplayIndex);
    setSentenceIndex(getPracticeIndexForDisplayIndex(displayItems, resolvedDisplayIndex));
    setRepeatProgress({ key: '', count: 0 });
    setSpeechSide('question');
    speechAnswerViewedRef.current = false;
    setRepeatEffect(null);
    setIsAutoRepeatCharging(false);
    setIsAutoRepeatBursting(false);
    setIsSentenceAdvancePending(false);
    sentenceAdvancePendingRef.current = false;
    if (sentenceAdvanceTimerRef.current) {
      clearTimeout(sentenceAdvanceTimerRef.current);
      sentenceAdvanceTimerRef.current = null;
    }
    return true;
  };

  const advanceToNextReadingItem = () => {
    const completePage = () => onCardComplete?.({
      targetRepeats: repeatPlanRef.current.pageTargetRepeats,
    });
    if (Array.isArray(displayItems) && displayItems.length > 0) {
      if (moveToDisplayIndex(displayIndex + 1)) {
        void playPracticeSound('endSession');
        return;
      }
      completePage();
      return;
    }

    if (sentenceIndex < sentences.length - 1) {
      void playPracticeSound('endSession');
      setSentenceIndex((prev) => prev + 1);
      setRepeatProgress({ key: '', count: 0 });
      setRepeatEffect(null);
      setIsAutoRepeatCharging(false);
      setIsAutoRepeatBursting(false);
      setSpeechSide('question');
      speechAnswerViewedRef.current = false;
      return;
    }

    completePage();
  };

  const renderContextItem = (item, index, area) => {
    if (!item?.text) return null;
    if (item.type === 'scene') {
      return <ReadingContentItem key={`${area}-scene-${index}`} item={item} />;
    }
    if (item.type === 'speech') {
      const speechParts = splitSpeechQA(item.text);
      const defaultSide = area === 'prev' ? 'answer' : 'question';
      const displayText = speechParts[defaultSide];
      return (
        <ReadingContentItem key={`${area}-speech-${index}`} item={item} characterNames={characterNames}
          speechText={displayText} className={audioFollow.classFor(item)} />
      );
    }
    return <ReadingContentItem key={`${area}-story-${index}`} item={item} className={audioFollow.classFor(item)} />;
  };

  useEffect(() => {
    const canvas = drawingCanvasRef.current;
    const frame = currentSectionRef.current;
    if (!canvas || !frame) return undefined;

    const resizeCanvas = () => {
      const rect = frame.getBoundingClientRect();
      const pixelRatio = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.round(rect.width * pixelRatio));
      const height = Math.max(1, Math.round(rect.height * pixelRatio));

      canvas.width = width;
      canvas.height = height;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const context = canvas.getContext('2d');
      if (context) {
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.clearRect(0, 0, rect.width, rect.height);
      }
    };

    resizeCanvas();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(frame);
    return () => resizeObserver.disconnect();
  }, [sentenceIndex, currentText, resetKey, card?.id, card?._id]);

  const getCurrentSentenceScore = (
    repeatCount = 1,
    completedWithoutFlip = false,
    speechSideOverride = speechSide
  ) => {
    const preview = getSentenceScorePreview?.({
      sentenceText: currentText,
      sentenceIndex: activeSentenceIndex,
      repeatCount,
      itemType: currentItem?.type || 'story',
      speechSide: isCurrentSpeech ? speechSideOverride : null,
      completedWithoutFlip,
    });
    const previewScore = Number(preview?.scoreDelta ?? preview);
    if (Number.isFinite(previewScore) && previewScore > 0) {
      return previewScore;
    }

    const safeRepeatCount = Math.max(1, Number(repeatCount) || 1);
    if (isCurrentSpeech) {
      const perRepeatScore = speechSideOverride === 'question' ? 2 : 1;
      return (perRepeatScore * safeRepeatCount) + (completedWithoutFlip ? 4 : 0);
    }
    return safeRepeatCount;
  };

  const scheduleSentenceAdvance = () => {
    sentenceAdvancePendingRef.current = true;
    setRepeatProgress({ key: currentRepeatKey, count: currentTargetRepeats });
    setIsSentenceAdvancePending(true);

    if (sentenceAdvanceTimerRef.current) {
      clearTimeout(sentenceAdvanceTimerRef.current);
    }
    sentenceAdvanceTimerRef.current = setTimeout(() => {
      sentenceAdvanceTimerRef.current = null;
      sentenceAdvancePendingRef.current = false;
      setIsSentenceAdvancePending(false);
      advanceToNextReadingItem();
    }, REPEAT_FEEDBACK_DURATION_MS);
  };

  const confirmOnceAnswer = () => {
    if (reviewedOnceRef.current.has(currentRepeatKey)) return;
    reviewedOnceRef.current.add(currentRepeatKey);
    setRepeatProgress({ key: currentRepeatKey, count: 1 });
    onSentenceRepeat?.({
      sentenceIndex: activeSentenceIndex,
      ...(currentChildId ? { childCardId: currentChildId } : {}),
      sentenceRepeatCount: 1, targetRepeats: 1, sentenceText: currentText,
      itemType: 'speech', speechSide: 'answer',
    });
  };

  const handleRepeat = () => {
    if (readOnly) return;
    if (sentenceAdvancePendingRef.current) return;
    if (isCurrentOnce && isCurrentPracticeTarget) {
      if (reviewedOnceRef.current.has(currentRepeatKey)) {
        // Keep the answer visible until the learner explicitly continues.
        sentenceAdvancePendingRef.current = true;
        advanceToNextReadingItem();
      } else {
        if (speechSide !== 'answer') playCardFlip();
        setSpeechSide('answer');
        speechAnswerViewedRef.current = true;
        confirmOnceAnswer();
      }
      return;
    }
    if (!isCurrentPracticeTarget) {
      advanceToNextReadingItem();
      return;
    }
    if (repeatInputLockedRef.current) return;

    const nextRepeat = sentenceRepeatCount + 1;
    const completedWithoutFlip = Boolean(
      canFlipCurrentSpeech &&
      nextRepeat >= currentTargetRepeats &&
      !speechAnswerViewedRef.current
    );
    const score = getCurrentSentenceScore(1, completedWithoutFlip);
    const repeatScore = isCurrentSpeech
      ? getCurrentSentenceScore(1, false, speechSide)
      : score;
    const answerRepeatScore = isCurrentSpeech
      ? getCurrentSentenceScore(1, false, 'answer')
      : repeatScore;
    repeatInputLockedRef.current = true;
    if (repeatInputLockTimerRef.current) {
      clearTimeout(repeatInputLockTimerRef.current);
    }
    repeatInputLockTimerRef.current = setTimeout(() => {
      repeatInputLockedRef.current = false;
      repeatInputLockTimerRef.current = null;
    }, REPEAT_INPUT_LOCK_MS);

    setRepeatEffect({
      id: `${Date.now()}-${activeSentenceIndex}-${nextRepeat}`,
      repeatDelta: 1,
      lengthBonus: !isCurrentSpeech
        ? Math.max(0, repeatScore - 1)
        : 0,
      frontBonus: isCurrentSpeech && speechSide === 'question'
        ? Math.max(0, repeatScore - answerRepeatScore)
        : 0,
      noFlipBonus: completedWithoutFlip
        ? Math.max(0, score - repeatScore)
        : 0,
    });
    onSentenceRepeat?.({
      sentenceIndex: activeSentenceIndex,
      ...(currentItem?.childCardId ? { childCardId: currentItem.childCardId } : {}),
      sentenceRepeatCount: nextRepeat,
      targetRepeats: currentTargetRepeats,
      sentenceText: currentText,
      ...(isCurrentSpeech ? {
        itemType: 'speech',
        speechSide,
        ...(completedWithoutFlip ? { completedWithoutFlip: true } : {}),
      } : {}),
    });

    if (nextRepeat < currentTargetRepeats) {
      setRepeatProgress({ key: currentRepeatKey, count: nextRepeat });
      return;
    }

    scheduleSentenceAdvance();
  };

  const handlePracticeAreaClick = () => {
    handleRepeat();
  };

  const handleCurrentSectionClick = (event) => {
    event.stopPropagation();
    handleRepeat();
  };

  const toggleSpeechSide = () => {
    if (readOnly || !canFlipCurrentSpeech || sentenceAdvancePendingRef.current) return;
    playCardFlip();
    const nextSide = speechSide === 'question' ? 'answer' : 'question';
    if (nextSide === 'answer') {
      speechAnswerViewedRef.current = true;
      if (isCurrentOnce && isCurrentPracticeTarget) confirmOnceAnswer();
    }
    setSpeechSide(nextSide);
    if (nextSide === 'answer' && canUseTts && isTtsAutoplayEnabled()) void readingAudio?.engine?.playUnit(currentItem);
    else readingAudio?.engine?.stop();
  };

  const handleSpeechFlipClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleSpeechSide();
  };

  useEffect(() => {
    if (!keyboardShortcutsEnabled || readOnly) return undefined;

    const handleKeyboardShortcut = (event) => {
      if (event.defaultPrevented || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      const key = String(event.key || '').toLowerCase();
      if (event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if (event.target?.closest?.('button') && (key === 'enter' || key === ' ')) return;
      if (key === 'escape') return;

      const isHorizontal =
        ['a', 'arrowleft', '4', 'd', 'arrowright', '6'].includes(key) ||
        ['Numpad4', 'Numpad6'].includes(event.code);
      if (isHorizontal && canFlipCurrentSpeech) {
        event.preventDefault();
        toggleSpeechSide();
        return;
      }

      if (key.length === 1 || key.startsWith('arrow') || key === 'enter' || key === ' ') {
        event.preventDefault();
        handleRepeat();
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => window.removeEventListener('keydown', handleKeyboardShortcut);
  });

  const cancelAutoRepeatCharge = () => {
    if (autoRepeatTimerRef.current) {
      clearTimeout(autoRepeatTimerRef.current);
      autoRepeatTimerRef.current = null;
    }
    autoRepeatPointerStartRef.current = null;
    setIsAutoRepeatCharging(false);
  };

  const handleAutoRepeat = () => {
    if (isCurrentOnce) return;
    if (sentenceAdvancePendingRef.current) return;
    const remainingRepeats = Math.max(0, currentTargetRepeats - sentenceRepeatCount);
    if (readOnly || !autoRepeatAvailable || !isCurrentPracticeTarget || remainingRepeats <= 0) return;
    const completedWithoutFlip = Boolean(
      canFlipCurrentSpeech &&
      !speechAnswerViewedRef.current
    );

    onSentenceAutoRepeat?.({
      sentenceIndex: activeSentenceIndex,
      ...(currentItem?.childCardId ? { childCardId: currentItem.childCardId } : {}),
      fromRepeatCount: sentenceRepeatCount,
      targetRepeats: currentTargetRepeats,
      sentenceText: currentText,
      ...(isCurrentSpeech ? {
        itemType: 'speech',
        speechSide,
        ...(completedWithoutFlip ? { completedWithoutFlip: true } : {}),
      } : {}),
    });

    setIsAutoRepeatBursting(false);
    requestAnimationFrame(() => setIsAutoRepeatBursting(true));
    if (isCurrentSpeech) {
      const repeatScore = getCurrentSentenceScore(remainingRepeats, false, speechSide);
      const answerRepeatScore = getCurrentSentenceScore(remainingRepeats, false, 'answer');
      const totalScore = getCurrentSentenceScore(remainingRepeats, completedWithoutFlip, speechSide);
      setRepeatEffect({
        id: `${Date.now()}-${activeSentenceIndex}-auto`,
        repeatDelta: remainingRepeats,
        frontBonus: speechSide === 'question'
          ? Math.max(0, repeatScore - answerRepeatScore)
          : 0,
        noFlipBonus: completedWithoutFlip
          ? Math.max(0, totalScore - repeatScore)
          : 0,
      });
    }
    scheduleSentenceAdvance();
  };

  const handleStatusPointerDown = (event) => {
    event.stopPropagation();
    if (isCurrentOnce) return;
    if (readOnly || !autoRepeatAvailable || !isCurrentPracticeTarget || sentenceRepeatCount >= currentTargetRepeats) return;

    cancelAutoRepeatCharge();
    suppressNextStatusClickRef.current = false;
    autoRepeatPointerStartRef.current = {
      x: Number(event.clientX) || 0,
      y: Number(event.clientY) || 0,
    };
    setIsAutoRepeatCharging(true);
    autoRepeatTimerRef.current = setTimeout(() => {
      autoRepeatTimerRef.current = null;
      setIsAutoRepeatCharging(false);
      suppressNextStatusClickRef.current = true;
      handleAutoRepeat();
    }, autoRepeatHoldMs);
  };

  const handleStatusPointerCancel = (event) => {
    event.stopPropagation();
    cancelAutoRepeatCharge();
  };

  const handleStatusClick = (event) => {
    event.stopPropagation();
    if (suppressNextStatusClickRef.current) {
      suppressNextStatusClickRef.current = false;
      return;
    }
    if (autoRepeatPointerStartRef.current || isAutoRepeatCharging) return;
    handleRepeat();
  };

  const handleStatusPointerMove = (event) => {
    if (!autoRepeatPointerStartRef.current) return;

    const deltaX = Math.abs((Number(event.clientX) || 0) - autoRepeatPointerStartRef.current.x);
    const deltaY = Math.abs((Number(event.clientY) || 0) - autoRepeatPointerStartRef.current.y);
    if (deltaX > 12 || deltaY > 12) {
      event.stopPropagation();
      cancelAutoRepeatCharge();
    }
  };

  const handleBrowserTtsClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canUseTts) {
      onMembershipRequired?.();
      return;
    }

    if (isBrowserSpeaking) {
      stopBrowserSpeech();
      return;
    }

    void readingAudio?.engine?.playUnit(currentItem);
  };

  const handleContentKeyDown = (event) => {
    if (readOnly) return;

    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleRepeat();
  };

  const getCanvasPoint = (event) => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    return {
      x: (Number(event.clientX) || 0) - rect.left,
      y: (Number(event.clientY) || 0) - rect.top,
    };
  };

  const drawToPoint = (point) => {
    const canvas = drawingCanvasRef.current;
    const state = drawingStateRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context || !state.isDrawing || !point) return;

    context.strokeStyle = '#ff3b30';
    context.lineWidth = 4;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.beginPath();
    context.moveTo(state.lastX, state.lastY);
    context.lineTo(point.x, point.y);
    context.stroke();
    state.lastX = point.x;
    state.lastY = point.y;
  };

  const drawPoint = (point) => {
    const canvas = drawingCanvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context || !point) return;

    context.fillStyle = '#ff3b30';
    context.beginPath();
    context.arc(point.x, point.y, 2.5, 0, Math.PI * 2);
    context.fill();
  };

  const handleDrawingPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (readOnly) return;

    const point = getCanvasPoint(event);
    if (!point) return;

    drawPoint(point);
    drawingStateRef.current = { isDrawing: true, lastX: point.x, lastY: point.y };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleDrawingPointerMove = (event) => {
    if (!drawingStateRef.current.isDrawing) return;
    event.preventDefault();
    event.stopPropagation();
    drawToPoint(getCanvasPoint(event));
  };

  const stopDrawing = (event) => {
    event?.stopPropagation();
    drawingStateRef.current.isDrawing = false;
  };

  return (
    <div
      className="reading-practice-shell"
      style={{ '--reading-repeat-feedback-duration': `${REPEAT_FEEDBACK_DURATION_MS}ms` }}
    >
      <div className="reading-practice-card">
        <div className="reading-practice-top">
          <button
            type="button"
            className="reading-nav-button"
            onClick={onSwipeRight}
            title="Previous card"
            aria-label="Previous card"
          >
            <ArrowLeft size={22} strokeWidth={2.4} aria-hidden="true" />
          </button>
          <div className="reading-practice-title-block">
            <div className="reading-practice-kicker">Reading</div>
            <h3 className="reading-practice-title">{title}</h3>
          </div>
          <button
            type="button"
            className="reading-nav-button"
            onClick={onSwipeLeft}
            title="Next card"
            aria-label="Next card"
          >
            <ArrowRight size={22} strokeWidth={2.4} aria-hidden="true" />
          </button>
        </div>

        <div
          className={`reading-practice-content ${readOnly ? 'is-read-only' : ''}`.trim()}
          ref={audioFollow.ref}
          role="button"
          tabIndex={readOnly ? -1 : 0}
          onClick={handlePracticeAreaClick}
          onKeyDown={handleContentKeyDown}
          aria-label={readOnly ? 'Review previous reading card' : 'Mark current reading sentence repeated'}
          aria-disabled={readOnly}
        >
          {displayItems.map((item, index) => {
            const key = item.childCardId || `${item.segmentIndex}:${item.sentenceIndex || 0}`;
            if (index !== displayIndex) {
              const area = index < displayIndex ? 'prev' : 'next';
              return <div key={key} className={`reading-practice-section reading-practice-section-${area}`}>
                {renderContextItem(item, index, area)}
              </div>;
            }
            return (
              <div
                key={key}
                ref={currentSectionRef}
                data-reading-position={`${currentItem?.segmentIndex}:${currentItem?.sentenceIndex || 0}`}
                className={[
                  audioFollow.classFor(currentItem),
                  'reading-practice-section',
                  'reading-practice-section-current',
                  isCurrentSpeech ? `reading-practice-current-speech speaker-${currentSpeaker.toLowerCase()} speech-side-${speechSide}` : '',
                  !isCurrentPracticeTarget ? 'reading-practice-section-pause' : '',
                  repeatEffect ? 'reading-repeat-pop' : '',
                ].filter(Boolean).join(' ')}
                onClick={handleCurrentSectionClick}
              >
                <div className={`reading-practice-drawing-frame${!isCurrentSpeech && !readOnly ? ' has-drawing-cue' : ''}`}>
                  {!isCurrentSpeech && !readOnly && (
                    <span className="reading-drawing-cue" title="Drawing canvas" aria-hidden="true">
                      <Pencil size={16} strokeWidth={2} />
                    </span>
                  )}
                  {isCurrentSpeech ? (
                    <div className={`reading-practice-speech-row is-current speaker-${currentSpeaker.toLowerCase()}`}>
                      <span className="reading-practice-speaker-name">{characterNames?.[currentSpeaker] || currentSpeaker}</span>
                      {canFlipCurrentSpeech ? (
                        <div className="reading-practice-speech-flip-row">
                          <button
                            type="button"
                            className="reading-practice-speech-flip-button"
                            onClick={handleSpeechFlipClick}
                            disabled={isSentenceAdvancePending}
                            aria-label="Flip dialog backward"
                            title="Flip dialog"
                          >
                            <ArrowLeft size={22} strokeWidth={2.5} aria-hidden="true" />
                          </button>
                          <ReadingSpeechBubble name={characterNames?.[currentSpeaker] || currentSpeaker} speaker={currentSpeaker} onClick={handleCurrentSectionClick} showAvatar={false}>
                            <MarkdownText text={currentDisplayText} />
                          </ReadingSpeechBubble>
                          <button
                            type="button"
                            className="reading-practice-speech-flip-button"
                            onClick={handleSpeechFlipClick}
                            disabled={isSentenceAdvancePending}
                            aria-label="Flip dialog forward"
                            title="Flip dialog"
                          >
                            <ArrowRight size={22} strokeWidth={2.5} aria-hidden="true" />
                          </button>
                        </div>
                      ) : (
                        <ReadingSpeechBubble name={characterNames?.[currentSpeaker] || currentSpeaker} speaker={currentSpeaker} onClick={handleCurrentSectionClick} showAvatar={false}>
                          <MarkdownText text={currentDisplayText} />
                        </ReadingSpeechBubble>
                      )}
                    </div>
                  ) : (
                    <MarkdownText text={currentText} />
                  )}
                  {!isCurrentSpeech && ' '}
                  {isCurrentPracticeTarget ? (
                    <span className="reading-practice-session-tools">
                      {isCurrentSpeech && <ReadingSpeechAvatar name={characterNames?.[currentSpeaker] || currentSpeaker} />}
                      <span
                        key={currentRepeatKey}
                        className={[
                          'reading-practice-session-status',
                          repeatEffect ? 'repeat-burst' : '',
                          isAutoRepeatCharging ? 'auto-repeat-charging' : '',
                          isAutoRepeatBursting ? 'auto-repeat-burst' : '',
                        ].filter(Boolean).join(' ')}
                        aria-label="Current sentence repeat progress"
                        title={readOnly || !autoRepeatAvailable ? 'Review previous reading sentence' : 'Hold for auto sentence repeat'}
                        onClick={handleStatusClick}
                        onPointerDown={handleStatusPointerDown}
                        onPointerUp={handleStatusPointerCancel}
                        onPointerMove={handleStatusPointerMove}
                        onPointerLeave={handleStatusPointerCancel}
                        onPointerCancel={handleStatusPointerCancel}
                        onAnimationEnd={() => {
                          setIsAutoRepeatBursting(false);
                          setRepeatEffect(null);
                        }}
                      >
                        ({sentenceRepeatCount}/{currentTargetRepeats})
                        {repeatEffect && (
                          <span className="reading-repeat-score-pop" key={repeatEffect.id}>
                            +{repeatEffect.repeatDelta}
                          </span>
                        )}
                      </span>
                      {(!isCurrentSpeech || speechSide === 'answer') && (
                        <button
                          type="button"
                          className={`reading-browser-tts-button ${isBrowserSpeaking ? 'is-speaking' : ''} ${!canUseTts ? 'is-locked' : ''}`.trim()}
                          onClick={handleBrowserTtsClick}
                          aria-label={t(!canUseTts
                            ? 'browserTts.membership'
                            : isBrowserSpeaking
                              ? 'browserTts.stop'
                              : 'browserTts.play')}
                          title={t(!canUseTts
                            ? 'browserTts.membership'
                            : isBrowserSpeaking
                              ? 'browserTts.stop'
                              : 'browserTts.play')}
                          aria-pressed={isBrowserSpeaking}
                        >
                          {!canUseTts ? (
                            <LockKeyhole size={15} aria-hidden="true" />
                          ) : isBrowserSpeaking ? (
                            <Square size={14} fill="currentColor" aria-hidden="true" />
                          ) : (
                            <Play size={15} fill="currentColor" aria-hidden="true" />
                          )}
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="reading-practice-session-tools">
                      {isCurrentSpeech && <ReadingSpeechAvatar name={characterNames?.[currentSpeaker] || currentSpeaker} />}
                      <span className="reading-practice-continue-cue">Tap to continue</span>
                      <button type="button" className="reading-audio-single" onClick={handleBrowserTtsClick} aria-label={readingAudioText('문장 듣기', 'Play sentence')}><Play size={14} /></button>
                    </span>
                  )}
                  {isCurrentPracticeTarget && repeatEffect && (
                    repeatEffect.lengthBonus > 0 || repeatEffect.frontBonus > 0 || repeatEffect.noFlipBonus > 0
                  ) && (
                    <span className="reading-repeat-bonus-pop" key={`${repeatEffect.id}-bonus`} role="status">
                      {repeatEffect.lengthBonus > 0 && (
                        <span>{t(
                          repeatEffect.lengthBonus >= 2 ? 'readingSpeech.longSentenceBonus' : 'readingSpeech.midSentenceBonus',
                          undefined, { score: repeatEffect.lengthBonus }
                        )}</span>
                      )}
                      {repeatEffect.frontBonus > 0 && (
                        <span>{t('readingSpeech.frontBonus', undefined, { score: repeatEffect.frontBonus })}</span>
                      )}
                      {repeatEffect.noFlipBonus > 0 && (
                        <span>{t('readingSpeech.noFlipBonus', undefined, { score: repeatEffect.noFlipBonus })}</span>
                      )}
                    </span>
                  )}
                  {!isCurrentSpeech && (
                    <canvas
                      ref={drawingCanvasRef}
                      className="reading-practice-drawing-canvas"
                      aria-label="Draw on current reading sentence"
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={handleDrawingPointerDown}
                      onPointerMove={handleDrawingPointerMove}
                      onPointerUp={stopDrawing}
                      onPointerLeave={stopDrawing}
                      onPointerCancel={stopDrawing}
                      role="img"
                    >
                      Drawing layer
                    </canvas>
                  )}
                </div>
              </div>

            );
          })}
        </div>
      </div>

    </div>
  );
}

export default ReadingArticlePractice;
